/**
 * AdminLayoutShell · v2.7.43 (2026-02-21)
 * ═════════════════════════════════════════════════════════════════════
 * Square-style admin shell.
 *
 *  ┌──────────────────────────────────────────────────────────────────┐
 *  │ <Sidebar>    │ <existing page top bar (Logo · Switch Mode · …)> │
 *  │ logo at top  │ <DispatchBar (this file · horizontal, w-full)>   │
 *  │              │ <page content / widgets>                         │
 *  │ nav items    │                                                  │
 *  │              │                                                  │
 *  │ ────────     │                                                  │
 *  │ theme switch │                                                  │
 *  └──────────────────────────────────────────────────────────────────┘
 *
 * Multi-axis drag-and-drop docking (v2.7.41) and the role-default
 * profiles (v2.7.42) are retired in favor of a single canonical layout
 * that matches Square's web admin. Role gating in `buildAdminMenus()`
 * still controls WHICH items each role sees — super_user always sees
 * everything, dispatcher gets the operational subset, etc.
 *
 * Mobile: sidebar collapses to a hamburger drawer beneath `md:`. Body
 * padding is removed and the sidebar overlays the page on toggle.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wifi, X, RotateCcw, ChevronDown, ChevronRight, ChevronLeft, Check, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import VersionBadge from "@/components/VersionBadge";
import useAdminPresence from "@/hooks/useAdminPresence";
import { buildAdminMenus, navigateAdmin } from "@/components/admin/adminNavTree";
import ThemeSwitcher from "@/components/admin/ThemeSwitcher";
import LocaleSwitcher from "@/components/LocaleSwitcher";   // v2.7.45+E9 · i18n trigger
import AdminFooter from "@/components/admin/AdminFooter";
import OperatorBadge from "@/components/admin/OperatorBadge";
import SidebarProfileFooter from "@/components/admin/SidebarProfileFooter";
import { useSidebarToggle } from "@/contexts/SidebarToggleContext";
import { toast } from "sonner";
import adminApi from "@/lib/adminApi";
import { TAXI_COMMANDER_LOGO } from "@/lib/brandAssets";
import QuickBookSlideOver from "@/components/admin/QuickBookSlideOver";

const SIDEBAR_WIDTH = 360; // px · widened to let the full-width Taxi-Commander logo render at the +85% XL size
const MICRO_SIDEBAR_WIDTH = 72; // v2.7.65+E141 · icon-only micro rail width
const STORAGE_KEY = "admin-sidebar-collapse-v2"; // {menuId: bool}

// v2.7.65+E140 · Admin Console redesign — clean white nav rows in dark
// theme (translucent hover/active per spec), matching neutral dark rows
// in light theme. Icons keep a subtle role tint so the visual hierarchy
// isn't lost, but the row text itself stays neutral / high-contrast.
const ICON_ACCENT_DARK = {
  red:     "text-red-300",
  rose:    "text-rose-300",
  amber:   "text-amber-300",
  indigo:  "text-indigo-300",
  violet:  "text-violet-300",
  sky:     "text-sky-300",
  cyan:    "text-yellow-300",
  fuchsia: "text-fuchsia-300",
  emerald: "text-emerald-300",
  green:   "text-emerald-300",
  pink:    "text-pink-300",
};
const iconAccentClass = (accent, active) => {
  if (active) return "text-amber-500 dark:text-amber-300";
  const dark = ICON_ACCENT_DARK[accent] || "dark:text-white/70";
  const light = {
    red: "text-red-600", rose: "text-rose-600", indigo: "text-indigo-600",
    sky: "text-sky-600", emerald: "text-emerald-600", green: "text-green-600",
    pink: "text-pink-600", fuchsia: "text-fuchsia-600", amber: "text-amber-600",
    violet: "text-violet-600", cyan: "text-yellow-600",
  }[accent] || "text-slate-500";
  return `${light} ${dark}`;
};
const accentTextClass = (accent, active) => {
  // Row label: pure white in dark theme, dark slate in light theme. No
  // per-accent tinting — the spec is explicit ("Clean white text across
  // all primary and secondary links").
  if (active) return "text-slate-900 dark:text-white";
  return "text-slate-700 dark:text-white";
};

// ─────────────────────────────────────────────────────────────────────
// AdminLayoutShell — renders the sidebar + the DispatchBar (sibling).
// Pages keep their existing top bar; this shell injects the sidebar
// fixed-left and reserves 260 px of body padding so content reflows.
// ─────────────────────────────────────────────────────────────────────
export default function AdminLayoutShell({
  activeTab, activeSub, onTopNav, adminRole, subsEnabled = false, adminRoles = [],
  unreadBadges = null, dispatchHealthy = true, badges = {},
  emailAccounts = [], emailUnread = {},
}) {
  const nav = useNavigate();
  const presence = useAdminPresence();
  const onlineCount = useMemo(
    () => Object.values(presence || {}).filter((p) => p?.status === "online").length,
    [presence],
  );

  // v2.7.45+E16 · Phase 6 · global Quick-Book slide-over (Ctrl/⌘+B)
  const [quickBookOpen, setQuickBookOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "b") {
        e.preventDefault();
        setQuickBookOpen((s) => !s);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // v2.7.50+E26 Phase 1 · Probe whether the current admin is the master
  // env-admin (the "primary system owner"). Used to render the
  // owner-only MASTER ACCESS sidebar entry. Endpoint is cheap and
  // self-authorises; defaults to false on any error so the link stays
  // hidden unless explicitly granted.
  const [isMasterOwner, setIsMasterOwner] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi.get("/admin/super-root/me");
        if (!cancelled) setIsMasterOwner(!!data?.is_master_owner);
      } catch {
        if (!cancelled) setIsMasterOwner(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // v2.7.65+E188 · Admin Console Logo — replaces the built-in
  // TAXI_COMMANDER_LOGO on the sidebar header when an admin uploads a
  // custom image via Website Management → Admin Console Logo. Public
  // GET so it hydrates on every admin route. Falls back silently to
  // TAXI_COMMANDER_LOGO if no custom logo is saved.
  // Listens for a `rtg:admin-console-logo-updated` window event so a
  // save from FrontEndTab lights up other open admin tabs immediately.
  const [customLogoUrl, setCustomLogoUrl] = useState("");
  useEffect(() => {
    let cancelled = false;
    const reload = async () => {
      try {
        const { data } = await adminApi.get("/admin/console-logo");
        if (!cancelled) setCustomLogoUrl(data?.url || "");
      } catch {
        if (!cancelled) setCustomLogoUrl("");
      }
    };
    reload();
    const onEvt = () => reload();
    window.addEventListener("rtg:admin-console-logo-updated", onEvt);
    return () => {
      cancelled = true;
      window.removeEventListener("rtg:admin-console-logo-updated", onEvt);
    };
  }, []);
  const sidebarLogoSrc = customLogoUrl || TAXI_COMMANDER_LOGO;

  // The 3-menu source. Role-gated: super_user gets all, dispatcher gets the
  // subset etc. Pulling DISPATCH and its children OUT into the standalone
  // DispatchBar means the sidebar shows operational TOP-LEVEL items only.
  const allMenus = useMemo(
    () => buildAdminMenus(adminRole, subsEnabled, adminRoles, badges, emailAccounts, emailUnread, isMasterOwner),
    [adminRole, subsEnabled, adminRoles, badges, emailAccounts, emailUnread, isMasterOwner],
  );

  // Dispatch items live in Menu 1 with `key: "dispatch"` heading + siblings.
  // For the new layout we split them out — DispatchBar shows the dispatch
  // heading + its 6 hoisted children; the sidebar shows the rest of Menu 1
  // + Menu 2 + Menu 3.
  const DISPATCH_KEYS = new Set(["dispatch", "live-map", "coordination", "pending", "book-ride", "chat", "helpdesk", "rides"]);
  const dispatchItems = useMemo(() => {
    const consoleMenu = allMenus.find((m) => m.id === "console");
    return consoleMenu ? consoleMenu.items.filter((i) => DISPATCH_KEYS.has(i.key)) : [];
  }, [allMenus]);
  const sidebarMenus = useMemo(() => {
    return allMenus.map((m) => {
      if (m.id !== "console") return m;
      return { ...m, items: m.items.filter((i) => !DISPATCH_KEYS.has(i.key)) };
    }).filter((m) => m.items.length > 0);
  }, [allMenus]);

  // Per-menu collapse state in sidebar (persists in localStorage)
  // v2.8+E340 · Developer Tools starts COLLAPSED by default (respects any
  // explicit user toggle already saved in localStorage).
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      if (!("developer-tools" in stored)) stored["developer-tools"] = true;
      return stored;
    } catch { return { "developer-tools": true }; }
  });
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(collapsed)); } catch { /* private */ }
  }, [collapsed]);

  // Mobile sidebar drawer state — now owned by the app-level
  // SidebarToggleProvider so the trigger button rendered higher in the
  // tree (in the page top bar) shares the same flag.
  // v2.7.45 · Also pulls desktopCollapsed so the Phase-3 floating arrow
  // can slide the sidebar off-screen and reclaim its 260 px on desktop.
  const { mobileOpen, setMobileOpen, desktopCollapsed, toggleDesktop, microMode, toggleMicro } = useSidebarToggle();
  const effectiveWidth = microMode ? MICRO_SIDEBAR_WIDTH : SIDEBAR_WIDTH;

  // v2.7.45 · Body padding management — DESKTOP ONLY (lg+ = ≥1024 px).
  // When `desktopCollapsed` is true the sidebar slides out of view and
  // the body padding collapses to 0 so console content fills 100% of the
  // freed horizontal space. The padding transition is animated to match
  // the sidebar's transform so the reflow looks intentional.
  // Below the lg breakpoint the sidebar is fully OFF-SCREEN until the
  // hamburger reveals it as an overlay (per Phase-2 spec). This means
  // tablets in portrait (768-1023 px) — most notably iPad portrait —
  // no longer have 260 px of horizontal real estate stolen.
  useEffect(() => {
    const apply = () => {
      const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
      const pad = isDesktop && !desktopCollapsed ? `${effectiveWidth}px` : "0px";
      document.body.style.paddingLeft = pad;
      document.body.style.transition = "padding-left 300ms ease-in-out";
    };
    apply();
    window.addEventListener("resize", apply);
    return () => {
      document.body.style.paddingLeft = "";
      document.body.style.transition = "";
      window.removeEventListener("resize", apply);
    };
  }, [desktopCollapsed, effectiveWidth]);

  const navHandle = useCallback((item) => {
    setMobileOpen(false);
    if (item.href) { nav(item.href); return; }
    if (onTopNav) navigateAdmin(onTopNav, { value: item.key, targetTab: item.targetTab, targetSub: item.targetSub });
  }, [nav, onTopNav]);

  const isActive = (item) => {
    if (item.targetTab && item.targetTab === activeTab) {
      if (item.targetSub === null) return !activeSub;
      if (item.targetSub == null) return true;
      return item.targetSub === activeSub;
    }
    return false;
  };

  const toggleCollapse = (menuId) => setCollapsed((c) => ({ ...c, [menuId]: !c[menuId] }));

  const resetLayout = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* */ }
    setCollapsed({});
    toast.success("Sidebar layout reset");
  };

  return (
    <>
      {/* v2.7.63+E47 · Persistent operator identity overlay — renders
          only when the current window is part of the 4-Window Command
          Console cluster (i.e. `?console=` present in URL). Zero-cost
          no-op on regular admin pages. */}
      <OperatorBadge/>

      {/* The mobile hamburger trigger is rendered by <SidebarMobileTrigger/>
          which the page top bar mounts (Phase-2 Task 1+4 — keeps the
          trigger in the top bar, not floating). State lives in the
          app-level SidebarToggleProvider. */}

      {/* ───────────── BACKDROP (mobile only when drawer open) ───────────── */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/60 z-40"
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-mobile-backdrop"
        />
      )}

      {/* ───────────── PHASE-3 · DESKTOP FLOATING ARROW TOGGLE ─────────────
          Sits flush against the right border of the sidebar (or against
          the left edge of the viewport when sidebar is collapsed off-
          screen). Hardware-accelerated `transform`-based slide so the
          arrow tracks the sidebar's exit / re-entry. Hidden on mobile —
          the hamburger in the top bar already covers that surface. */}
      <button
        type="button"
        onClick={toggleDesktop}
        aria-label={desktopCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        aria-expanded={!desktopCollapsed}
        title={desktopCollapsed ? "Show navigation" : "Hide navigation"}
        data-testid="sidebar-desktop-toggle"
        data-collapsed={desktopCollapsed ? "true" : "false"}
        className={`
          hidden lg:flex fixed top-1/2 -translate-y-1/2 z-50
          h-14 w-7 items-center justify-center
          rounded-r-md border border-l-0 border-zinc-300 dark:border-zinc-700
          bg-white dark:bg-zinc-950
          text-slate-700 dark:text-zinc-200
          hover:bg-amber-50 dark:hover:bg-zinc-900
          hover:text-amber-600 dark:hover:text-amber-300
          shadow-md hover:shadow-lg
          transition-all duration-300 ease-in-out
          focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60
        `}
        style={{ left: desktopCollapsed ? 0 : `${effectiveWidth}px` }}
      >
        {desktopCollapsed
          ? <ChevronRight className="h-4 w-4" aria-hidden="true"/>
          : <ChevronLeft className="h-4 w-4" aria-hidden="true"/>}
      </button>

      {/* ───────────── SIDEBAR — Square-style full height ───────────── */}
      <aside
        className={`
          fixed top-0 left-0 h-screen z-40 flex flex-col
          bg-white dark:bg-slate-950 border-r border-zinc-200 dark:border-white/10
          transition-transform duration-300 ease-in-out
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          ${desktopCollapsed ? "lg:-translate-x-full" : "lg:translate-x-0"}
        `}
        style={{ width: `${effectiveWidth}px` }}
        data-testid="admin-sidebar"
        data-micro={microMode ? "true" : "false"}
        aria-hidden={desktopCollapsed && !mobileOpen}
      >
        {/* ── HEADER · TAXI-COMMANDER · v2.7.46+E21 (micro-aware · E141) ── */}
        <div
          className={`relative flex items-stretch justify-center border-b border-slate-200 dark:border-zinc-800 flex-shrink-0 bg-slate-50 dark:bg-zinc-950 overflow-hidden ${microMode ? "py-2" : ""}`}
          style={microMode ? {} : { padding: 0, margin: 0 }}
          data-testid="sidebar-header"
        >
          <a
            href="/admin"
            className={`min-w-0 flex items-center justify-center group overflow-hidden ${microMode ? "" : "flex-1 items-stretch"}`}
            style={microMode ? {} : { padding: 0, margin: 0 }}
            data-testid="sidebar-logo"
            aria-label="Taxi Commander · Admin Console"
          >
            <img
              src={sidebarLogoSrc}
              alt="Admin Console Logo"
              className="block group-hover:opacity-90 transition-opacity mx-auto"
              style={microMode ? {
                width: "40px", height: "40px", objectFit: "contain", display: "block",
              } : {
                // v2.7.65+E176 · Restored to full container width now that
                // the logo is horizontal (crest + plaque side-by-side).
                // Landscape orientation naturally keeps the header short.
                width: "100%", maxWidth: "100%", height: "auto",
                objectFit: "contain", objectPosition: "center",
                margin: 0, padding: 0, display: "block",
              }}
              data-testid="sidebar-logo-img"
            />
          </a>
          {/* Mobile close button (absolute so it doesn't shrink the logo) */}
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            className="lg:hidden absolute top-2 right-2 text-white/80 hover:text-white p-1 bg-black/40 rounded-full"
            aria-label="Close navigation"
            data-testid="sidebar-close-mobile"
          >
            <X className="h-4 w-4"/>
          </button>
          {/* v2.7.65+E173 · Subtle 1px gold hairline that fades at both
              ends, seating the logo above the nav rail. Rendered as an
              absolute-positioned pseudo-strip so it does not affect the
              flex-height math of the header block above. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-px"
            style={{
              background:
                "linear-gradient(to right, transparent 0%, rgba(250,204,21,0.55) 20%, rgba(250,204,21,0.75) 50%, rgba(250,204,21,0.55) 80%, transparent 100%)",
            }}
            data-testid="sidebar-header-hairline"
          />
        </div>

        {/* ── STATUS STRIP · v1.1 · online · count (hidden in micro) ── */}
        {!microMode && (
          <div className="px-4 py-2 border-b border-zinc-200 dark:border-white/10 flex items-center gap-2 text-[10px] flex-shrink-0" data-testid="sidebar-status-strip">
            <VersionBadge inline/>
            <span className="text-zinc-300 dark:text-white/30">·</span>
            <span className={`flex items-center gap-1 font-bold uppercase tracking-widest ${dispatchHealthy ? "text-emerald-700 dark:text-emerald-300" : "text-red-600 dark:text-red-300"}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${dispatchHealthy ? "bg-emerald-500" : "bg-red-500"}`}/>
              {dispatchHealthy ? "Online" : "Offline"}
            </span>
            <span className="text-zinc-300 dark:text-white/30">·</span>
            <Wifi className="h-3 w-3 text-amber-500 dark:text-amber-400"/>
            <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40 text-[10px] font-extrabold tabular-nums" data-testid="sidebar-online-count">{onlineCount}</Badge>
          </div>
        )}

        {/* ── NAV BODY (scrollable) ── */}
        <nav className="flex-1 overflow-y-auto py-2" data-testid="sidebar-nav-body">
          {sidebarMenus.map((menu) => {
            const isCollapsed = !!collapsed[menu.id];
            return (
              <section key={menu.id} className="mb-1" data-testid={`sidebar-section-${menu.id}`}>
                {microMode ? (
                  <div className="mx-3 my-1 border-t border-zinc-200 dark:border-white/10"/>
                ) : (
                  <button
                    type="button"
                    onClick={() => toggleCollapse(menu.id)}
                    className="w-full flex items-center justify-between px-4 py-2 text-[10px] uppercase tracking-widest font-bold text-zinc-500 dark:text-white/60 hover:text-zinc-900 dark:hover:text-white transition-colors"
                    data-testid={`sidebar-section-toggle-${menu.id}`}
                  >
                    <span>{menu.title}</span>
                    {isCollapsed ? <ChevronRight className="h-3 w-3"/> : <ChevronDown className="h-3 w-3"/>}
                  </button>
                )}
                {(microMode || !isCollapsed) && (
                  <ul className="py-0.5">
                    {menu.items.map((item) => (
                      <SidebarItem
                        key={item.key}
                        item={item}
                        active={isActive(item)}
                        unreadBadges={unreadBadges}
                        onClick={() => navHandle(item)}
                        micro={microMode}
                      />
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </nav>

        {/* ── FOOTER · Presence + Reset + Theme Switcher (locked to bottom) ── */}
        <div className={`flex-shrink-0 border-t border-zinc-200 dark:border-white/10 ${microMode ? "px-1.5 py-2 space-y-1.5" : "px-3 py-3 space-y-2.5"} bg-white dark:bg-slate-950`}>
          {/* v2.7.65+E140 · Sidebar profile footer with presence dot + status selector */}
          <SidebarProfileFooter micro={microMode}/>
          {/* v2.7.65+E141 · Micro-mode toggle · shrink/expand icon-only rail */}
          <button
            type="button"
            onClick={toggleMicro}
            data-testid="sidebar-micro-toggle"
            title={microMode ? "Expand sidebar" : "Shrink to icons"}
            className={`w-full flex items-center justify-center gap-1.5 text-[10px] uppercase tracking-widest font-bold py-1.5 rounded border transition-colors ${
              microMode
                ? "border-amber-400/60 text-amber-600 dark:text-amber-300"
                : "border-zinc-200 dark:border-white/10 text-zinc-500 dark:text-white/60 hover:text-zinc-900 dark:hover:text-white hover:border-amber-400/60"
            }`}
          >
            {microMode ? <ChevronRight className="h-3 w-3"/> : <ChevronLeft className="h-3 w-3"/>}
            {!microMode && <span>Icon rail</span>}
          </button>
          {!microMode && (
            <>
              <button
                type="button"
                onClick={resetLayout}
                className="w-full text-[10px] uppercase tracking-widest font-bold text-zinc-500 dark:text-white/60 hover:text-zinc-900 dark:hover:text-white flex items-center justify-center gap-1.5 py-1.5 rounded border border-zinc-200 dark:border-white/10 hover:border-amber-400/60 transition-colors"
                data-testid="sidebar-reset-layout"
                title="Reset section collapse to defaults"
              >
                <RotateCcw className="h-3 w-3"/>Reset layout
              </button>
              <ThemeSwitcher/>
              {/* v2.7.45+E9 · WCAG-AA-compliant locale picker (6 languages) */}
              <div className="mt-2">
                <LocaleSwitcher/>
              </div>
            </>
          )}
        </div>
      </aside>

      {/* ───────────── HORIZONTAL DISPATCH BAR (w-full · below page top bar) ───────────── */}
      {dispatchItems.length > 0 && (
        <DispatchBar
          items={dispatchItems}
          isActive={isActive}
          unreadBadges={unreadBadges}
          onClick={navHandle}
        />
      )}
      {/* v2.7.45+E16 · Phase 6 · Global Quick-Book slide-over (⌘B / Ctrl+B) */}
      <QuickBookSlideOver
        open={quickBookOpen}
        onClose={() => setQuickBookOpen(false)}
        adminRole={adminRole}
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// SidebarMobileTrigger · v2.7.44
// Hamburger button intended to live IN the page top bar. Hidden on
// desktop (≥ 1024 px) because the sidebar is permanently visible
// there. Consumes SidebarToggleContext so wiring is automatic.
// ─────────────────────────────────────────────────────────────────────
export function SidebarMobileTrigger({ className = "" }) {
  const { toggleMobile } = useSidebarToggle();
  return (
    <button
      type="button"
      onClick={toggleMobile}
      className={`lg:hidden inline-flex items-center justify-center h-9 w-9 rounded border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 text-slate-700 dark:text-zinc-200 hover:text-amber-600 dark:hover:text-amber-300 hover:border-amber-400/60 transition-colors ${className}`}
      data-testid="sidebar-mobile-trigger"
      aria-label="Toggle navigation menu"
    >
      <HamburgerIcon className="h-4 w-4"/>
    </button>
  );
}

// Tiny inline hamburger so we don't grow the lucide-react import set unnecessarily.
function HamburgerIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <line x1="3" y1="6" x2="21" y2="6"/>
      <line x1="3" y1="12" x2="21" y2="12"/>
      <line x1="3" y1="18" x2="21" y2="18"/>
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────
// SidebarItem — single nav row. Square-style hover bar + active rail.
// ─────────────────────────────────────────────────────────────────────
function SidebarItem({ item, active, unreadBadges, onClick, micro = false }) {
  const Icon = item.Icon;
  const textCls = accentTextClass(item.accent, active);
  const iconCls = iconAccentClass(item.accent, active);
  const keyBadgeCount = item.badge && unreadBadges ? unreadBadges[item.badge] || 0 : 0;
  const directBadgeCount = typeof item.unreadCount === "number" ? item.unreadCount : 0;
  const badgeCount = keyBadgeCount + directBadgeCount;
  const [marking, setMarking] = useState(false);

  const onMarkRead = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (marking) return;
    setMarking(true);
    try {
      const { data } = await adminApi.post(`/admin/email/accounts/${item.markReadAccountId}/mark-all-read`);
      const n = data?.marked || 0;
      toast.success(n > 0 ? `Marked ${n} message${n === 1 ? "" : "s"} as read` : "Nothing to mark");
      window.dispatchEvent(new CustomEvent("rtg-email-mark-read"));
    } catch (err) {
      toast.error(`Mark-read failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setMarking(false);
    }
  };

  const showMarkAction = item.markReadAccountId && badgeCount > 0;

  // v2.7.65+E141 · Icon-only micro variant — narrow, centered, tooltip
  // exposes the full label. Badge collapses to a tiny red dot in the
  // corner so unread counts still surface without eating width.
  if (micro) {
    return (
      <li>
        <div className={`relative flex items-center justify-center transition-colors ${
          active ? "bg-amber-500/10 dark:bg-white/[0.20]" : "hover:bg-black/[0.04] dark:hover:bg-white/[0.12]"
        }`}>
          {active && (
            <span aria-hidden className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-amber-500 dark:bg-amber-400"/>
          )}
          <button
            type="button"
            onClick={onClick}
            data-testid={`sidebar-item-${item.key}`}
            title={item.label}
            aria-label={item.label}
            className="w-full grid place-items-center py-2.5"
          >
            {Icon ? <Icon className={`h-4 w-4 ${iconCls}`}/> : null}
            {badgeCount > 0 && (
              <span
                data-testid={`sidebar-badge-${item.key}`}
                className="absolute top-1.5 right-2 h-1.5 w-1.5 rounded-full bg-red-500"
                title={`${badgeCount} unread`}
              />
            )}
          </button>
        </div>
      </li>
    );
  }

  // v2.7.65+E140 · Spec-mandated row states:
  //   Default hover  → rgba(255,255,255,0.12) in dark, black/5 in light
  //   Active         → rgba(255,255,255,0.20) in dark, amber-500/10 in light
  //   Amber left-rail indicator on active for brand consistency
  return (
    <li>
      <div
        className={`
          group/row w-full flex items-stretch text-xs transition-colors relative
          ${active
            ? "bg-amber-500/10 dark:bg-white/[0.20] font-extrabold"
            : "hover:bg-black/[0.04] dark:hover:bg-white/[0.12] font-semibold"}
          ${textCls}
        `}
      >
        {active && (
          <span aria-hidden className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-amber-500 dark:bg-amber-400"/>
        )}
        <button
          type="button"
          onClick={onClick}
          data-testid={`sidebar-item-${item.key}`}
          className="flex-1 min-w-0 text-left flex items-center gap-2.5 px-5 py-2"
        >
          {Icon ? <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${iconCls}`}/> : null}
          <span className="tracking-wider truncate">{item.label}</span>
          {item.isEmailAlias && (
            <span
              className="ml-1 text-[8px] uppercase tracking-widest font-bold px-1 py-[1px] rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 flex-shrink-0"
              data-testid={`sidebar-alias-chip-${item.key}`}
              title="Alias mailbox"
            >
              alias
            </span>
          )}
          {badgeCount > 0 && (
            <Badge
              data-testid={`sidebar-badge-${item.key}`}
              className="ml-auto bg-red-500/20 text-red-600 dark:text-red-300 border-red-400/40 text-[9px] px-1 h-4 font-bold"
            >
              {badgeCount}
            </Badge>
          )}
        </button>
        {showMarkAction && (
          <button
            type="button"
            onClick={onMarkRead}
            disabled={marking}
            data-testid={`sidebar-markread-${item.key}`}
            title="Mark all as read"
            aria-label={`Mark all ${item.label} messages as read`}
            className={`
              opacity-0 group-hover/row:opacity-100 focus:opacity-100 focus-visible:opacity-100
              transition-opacity flex items-center justify-center px-2
              text-emerald-600 dark:text-emerald-300
              hover:bg-emerald-100 dark:hover:bg-emerald-900/30
              disabled:opacity-100 disabled:cursor-not-allowed
            `}
          >
            {marking
              ? <Loader2 className="h-3.5 w-3.5 animate-spin"/>
              : <Check className="h-3.5 w-3.5"/>}
          </button>
        )}
      </div>
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────
// DispatchBar — horizontal w-full strip beneath the page top bar.
// FUTURE-PROOFING: this is intentionally a SEPARATE component with a
// `layout` prop (currently always "horizontal"). To move it back into
// the sidebar, change AdminLayoutShell to NOT filter `DISPATCH_KEYS`
// out of the sidebar AND skip rendering DispatchBar — the items will
// naturally appear under the Admin Console section just like the
// other links. No style changes required.
// ─────────────────────────────────────────────────────────────────────
function DispatchBar({ items, isActive, unreadBadges, onClick, layout = "horizontal" }) {
  if (layout !== "horizontal") return null;
  return (
    <div
      className="w-full bg-white dark:bg-zinc-950 border-b border-zinc-200 dark:border-zinc-800 px-3 sm:px-4 py-2 flex items-center gap-1 sm:gap-2 overflow-x-auto scrollbar-thin"
      data-testid="dispatch-bar"
    >
      <span className="text-[10px] uppercase tracking-widest font-extrabold text-amber-600 dark:text-amber-300 mr-2 flex-shrink-0">
        Dispatch
      </span>
      {items.filter((it) => it.key !== "dispatch").map((item) => (
        <DispatchBarItem
          key={item.key}
          item={item}
          active={isActive(item)}
          unreadBadges={unreadBadges}
          onClick={() => onClick(item)}
        />
      ))}
    </div>
  );
}

function DispatchBarItem({ item, active, unreadBadges, onClick }) {
  const Icon = item.Icon;
  const cls = accentTextClass(item.accent, active);
  const badgeCount = item.badge && unreadBadges ? unreadBadges[item.badge] || 0 : 0;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`dispatch-bar-item-${item.key}`}
      className={`
        flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold tracking-wider rounded-md
        transition-colors whitespace-nowrap flex-shrink-0
        ${active
          ? "bg-amber-500/15 dark:bg-amber-400/15 border border-amber-500/40 dark:border-amber-400/40"
          : "border border-transparent hover:bg-zinc-100 dark:hover:bg-zinc-900"}
        ${cls}
      `}
    >
      {Icon ? <Icon className="h-3.5 w-3.5"/> : null}
      <span>{item.label}</span>
      {badgeCount > 0 && (
        <Badge className="bg-red-500/20 text-red-600 dark:text-red-300 border-red-400/40 text-[9px] px-1 h-4 font-bold">{badgeCount}</Badge>
      )}
    </button>
  );
}
